import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import { refrescarToken } from '../lib/api';
import { useColaStore } from '../lib/colaOffline';
import { SalaVivaSocket } from '../lib/ws';
import type { EstadoConexion, Presencia, SalaEvento } from '../lib/ws';
import {
  sesionKeys,
  type AportesRespuesta,
  type ActividadDetalle,
  type SesionDetalle,
  type Transcripcion,
} from '../lib/sesion';

/**
 * useSalaViva (hito M1 · "la sala viva").
 *
 * Conecta el WebSocket de una sesión y VUELCA cada evento del contrato a la
 * caché de TanStack Query, de modo que las superficies (Pantalla/Cabina/
 * Bolsillo) se actualizan en < 1 s sin re-pedir a la API:
 *
 *   · aporte_nuevo    -> append al cache de aportes (dedup por id)
 *   · agregado        -> reemplaza el agregado del cache de aportes
 *   · actividad_estado-> parcha el detalle de la actividad y la agenda
 *   · presencia       -> estado local del hook (C7: solo agregados por grupo)
 *   · cierre          -> marca cerrada + expone artefacto para la revelación
 *
 * MODO DEGRADADO (ciudadano de primera clase): si el WS no conecta tras varios
 * intentos, `estadoConexion` pasa a 'degradado'; las páginas reactivan su
 * polling existente de forma transparente y lo indican en la UI. El socket
 * sigue reintentando en segundo plano para recuperar el vivo.
 *
 * TIMER: por cada mensaje se recalibra el offset con el servidor (server_now)
 * para que el Temporizador tenga autoridad de servidor.
 */
export interface SalaViva {
  estadoConexion: EstadoConexion;
  enVivo: boolean;
  degradado: boolean;
  /** ms a sumar a Date.now() para obtener la hora del servidor. */
  offsetServidorMs: number;
  /** Presencia agregada por grupo (C7); null hasta el primer evento. */
  presencia: Presencia | null;
  /** Último cierre recibido, para la revelación del artefacto en Pantalla. */
  ultimoCierre: { actividadId: string; artefactoId: string } | null;
  /**
   * Transcripciones ya listas por aporte (M2). El Bolsillo lo usa para mostrar
   * el texto de SU evidencia sin pedir la lista de aportes; la Cabina las lee
   * ya parchadas sobre cada aporte de la caché. Clave: aporte_id.
   */
  transcripciones: Record<string, Transcripcion>;
}

const AGREGADO_VACIO = { total: 0, por_tipo: {}, por_grupo: [] };

export function useSalaViva(sesionId: string | null | undefined): SalaViva {
  const qc = useQueryClient();
  const [estadoConexion, setEstadoConexion] = useState<EstadoConexion>('conectando');
  const [presencia, setPresencia] = useState<Presencia | null>(null);
  const [ultimoCierre, setUltimoCierre] = useState<SalaViva['ultimoCierre']>(null);
  const [transcripciones, setTranscripciones] = useState<Record<string, Transcripcion>>({});
  const [offsetServidorMs, setOffsetServidorMs] = useState(0);
  const offsetRef = useRef(0);

  // Recalibra la hora de servidor sin tormenta de renders (solo si cambia >0.8s).
  const aplicarOffset = useCallback((serverNow?: string) => {
    if (!serverNow) return;
    const t = Date.parse(serverNow);
    if (Number.isNaN(t)) return;
    const off = t - Date.now();
    if (Math.abs(off - offsetRef.current) > 800) {
      offsetRef.current = off;
      setOffsetServidorMs(off);
    }
  }, []);

  const onEvento = useCallback(
    (ev: SalaEvento) => {
      aplicarOffset(ev.server_now);

      switch (ev.type) {
        case 'aporte_nuevo': {
          qc.setQueryData<AportesRespuesta>(sesionKeys.aportes(ev.actividad_id), (prev) => {
            const base = prev ?? { aportes: [], agregado: { ...AGREGADO_VACIO } };
            if (base.aportes.some((a) => a.id === ev.aporte.id)) return base;
            return { ...base, aportes: [...base.aportes, ev.aporte] };
          });
          break;
        }

        case 'agregado': {
          qc.setQueryData<AportesRespuesta>(sesionKeys.aportes(ev.actividad_id), (prev) => {
            const base = prev ?? { aportes: [], agregado: { ...AGREGADO_VACIO } };
            return { ...base, agregado: ev.agregado };
          });
          break;
        }

        case 'actividad_estado': {
          qc.setQueryData<ActividadDetalle>(sesionKeys.actividad(ev.actividad_id), (prev) =>
            prev
              ? {
                  ...prev,
                  estado: ev.estado,
                  abierta_at: ev.abierta_at,
                  cerrada_at: ev.cerrada_at,
                  duracion_min: ev.duracion_min ?? prev.duracion_min,
                  pausa_acumulado_s: ev.pausa_acumulado_s,
                }
              : prev,
          );
          // Refleja el estado en la agenda de la sesión (foco/apertura/pausa).
          if (sesionId) {
            qc.setQueryData<SesionDetalle>(sesionKeys.sesion(sesionId), (prev) =>
              prev
                ? {
                    ...prev,
                    actividades: prev.actividades.map((a) =>
                      a.id === ev.actividad_id ? { ...a, estado: ev.estado } : a,
                    ),
                  }
                : prev,
            );
            // Si aún no teníamos el detalle cacheado, que las páginas lo traigan.
            void qc.invalidateQueries({ queryKey: sesionKeys.sesion(sesionId) });
          }
          break;
        }

        case 'presencia': {
          setPresencia({ conectados_total: ev.conectados_total, por_grupo: ev.por_grupo });
          break;
        }

        case 'cierre': {
          qc.setQueryData<ActividadDetalle>(sesionKeys.actividad(ev.actividad_id), (prev) =>
            prev ? { ...prev, estado: 'cerrada' } : prev,
          );
          if (sesionId) {
            qc.setQueryData<SesionDetalle>(sesionKeys.sesion(sesionId), (prev) =>
              prev
                ? {
                    ...prev,
                    actividades: prev.actividades.map((a) =>
                      a.id === ev.actividad_id ? { ...a, estado: 'cerrada' } : a,
                    ),
                  }
                : prev,
            );
          }
          // Trae total/artefacto definitivos del acta congelada (C6).
          void qc.invalidateQueries({ queryKey: sesionKeys.actividad(ev.actividad_id) });
          setUltimoCierre({ actividadId: ev.actividad_id, artefactoId: ev.artefacto_id });
          break;
        }

        case 'sala_diarizada': {
          // El micrófono de sala terminó de procesarse: las intervenciones ya
          // están en el acta — que la Cabina las traiga sin esperar el poll.
          void qc.invalidateQueries({ queryKey: ['aportes', ev.actividad_id] });
          break;
        }

        case 'ronda': {
          // Ronda dirigida (ADR-0005 §2): parche directo del detalle para que
          // el Bolsillo cambie de paso en < 1 s, + invalidación de respaldo.
          qc.setQueryData<ActividadDetalle>(
            sesionKeys.actividad(ev.actividad_id),
            (prev) => (prev ? { ...prev, ronda: ev.seccion } : prev),
          );
          void qc.invalidateQueries({ queryKey: sesionKeys.actividad(ev.actividad_id) });
          break;
        }

        case 'proyeccion': {
          // Switch del facilitador: la Pantalla cambia entre Explicar y
          // Tablero en < 1 s. Parche directo + invalidación de respaldo.
          qc.setQueryData<ActividadDetalle>(
            sesionKeys.actividad(ev.actividad_id),
            (prev) => (prev ? { ...prev, proyeccion_modo: ev.modo } : prev),
          );
          void qc.invalidateQueries({ queryKey: sesionKeys.actividad(ev.actividad_id) });
          break;
        }

        case 'moderacion': {
          // Moderación (M2): parcheo del flag `oculto` en toda caché de aportes
          // que contenga el aporte — Pantalla lo excluye y Cabina lo atenúa
          // en < 1 s, sin esperar el próximo poll.
          qc.setQueriesData<AportesRespuesta>({ queryKey: ['aportes'] }, (prev) => {
            if (!prev || !prev.aportes.some((a) => a.id === ev.aporte_id)) return prev;
            return {
              ...prev,
              aportes: prev.aportes.map((a) =>
                a.id === ev.aporte_id ? { ...a, oculto: Boolean(ev.oculto) } : a,
              ),
            };
          });
          break;
        }

        case 'transcripcion_lista': {
          const trans: Transcripcion = { texto: ev.texto, confianza: ev.confianza };
          // Parchamos el aporte en TODA caché de aportes que lo contenga (la
          // clave es por actividad y el evento no la trae). Así la lista de la
          // Cabina muestra la transcripción citable in situ.
          qc.setQueriesData<AportesRespuesta>({ queryKey: ['aportes'] }, (prev) => {
            if (!prev || !prev.aportes.some((a) => a.id === ev.aporte_id)) return prev;
            return {
              ...prev,
              aportes: prev.aportes.map((a) =>
                a.id === ev.aporte_id ? { ...a, transcripcion: trans } : a,
              ),
            };
          });
          // Y en el mapa local, para que el Bolsillo muestre el texto de SU
          // evidencia sin cargar la lista de aportes.
          setTranscripciones((prev) => ({ ...prev, [ev.aporte_id]: trans }));
          break;
        }
      }
    },
    [qc, sesionId, aplicarOffset],
  );

  const onEstado = useCallback((e: EstadoConexion) => {
    setEstadoConexion(e);
    // Al recuperar el vivo, drenamos la cola offline (idempotente por contrato).
    if (e === 'en_vivo') void useColaStore.getState().flush();
  }, []);

  useEffect(() => {
    if (!sesionId) return;
    const sock = new SalaVivaSocket({
      sesionId,
      getToken: () => useAuthStore.getState().token,
      // El token del handshake vence a los minutos y nadie lo renueva por el
      // socket: sin esto, un rechazo de auth se reintenta para siempre con el
      // mismo JWT muerto y la persona pierde el vivo el resto de la jornada.
      refrescarToken,
      onEvento,
      onEstado,
    });
    sock.conectar();
    return () => sock.cerrar();
  }, [sesionId, onEvento, onEstado]);

  return {
    estadoConexion,
    enVivo: estadoConexion === 'en_vivo',
    degradado: estadoConexion === 'degradado',
    offsetServidorMs,
    presencia,
    ultimoCierre,
    transcripciones,
  };
}

/** Selector reactivo del nº de aportes pendientes (opcionalmente por actividad). */
export function useColaPendientes(actividadId?: string): number {
  return useColaStore((s) =>
    actividadId ? s.items.filter((i) => i.actividadId === actividadId).length : s.items.length,
  );
}
